/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, {
  ClassAttributes,
  PropsWithChildren,
  TableHTMLAttributes,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { CodeProps, ReactMarkdownProps } from 'react-markdown/lib/ast-to-react';
import gfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import clsx from 'clsx';
import Highlight, { defaultProps, Prism } from 'prism-react-renderer';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import { TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import useStyles, { BallerinaPrismTheme } from './style';
import './prism-ballerina';

(typeof global !== 'undefined' ? (global as any) : window).Prism = Prism;

function markdownTableCell(props: any) {
  return (
    <TableCell>
      <Typography>{props && props.children}</Typography>
    </TableCell>
  );
}

function markdownTableRow(props: any) {
  const { children } = props;
  return (
    <TableRow>
      {children.map(
        (child: any) => child.props && markdownTableCell(child.props)
      )}
    </TableRow>
  );
}

function markdownTableBody(props: any) {
  const { children } = props;
  return (
    <TableBody>
      {children.map(
        (child: any) => child.props && markdownTableRow(child.props)
      )}
    </TableBody>
  );
}

function markdownTableHead(props: any) {
  const { children } = props;
  return (
    <TableHead>
      {children.map(
        (child: any) => child.props && markdownTableRow(child.props)
      )}
    </TableHead>
  );
}

function MarkdownCode(props: PropsWithChildren<CodeProps>) {
  const { inline, className, children } = props;
  let language: any;
  const classes = useStyles();
  // Extract langauge from className
  const match = /language-(\w+)/.exec(className || '');
  // eslint-disable-next-line prefer-const
  language = match != null ? match[1] : 'markdown';
  // Remove the \n at the end
  const code = String(children).replace(/\n$/, '');

  return !inline ? (
    <Highlight
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...defaultProps}
      theme={BallerinaPrismTheme}
      language={language}
      code={code}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-shadow */}
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre className={clsx(className, classes.codeBlock)} style={style}>
          <div className={classes.codeBlockHorizontalNumberLine} />

          {tokens.map((line, lineNumber) => {
            const { className: linePropClassName } = getLineProps({
              line,
              key: lineNumber,
            });

            return (
              // eslint-disable-next-line react/jsx-key
              <div className={clsx(linePropClassName, classes.codeBlockLine)}>
                <span>{lineNumber + 1}</span>
                {line.map((token, key) => (
                  // eslint-disable-next-line react/jsx-key
                  <span {...getTokenProps({ token, key })} />
                ))}
              </div>
            );
          })}
        </pre>
      )}
    </Highlight>
  ) : (
    <code className={classes.inlineCode} {...{ inline, children }} />
  );
}

function MarkdownTable(
  props: PropsWithChildren<
    ClassAttributes<HTMLTableElement> &
      TableHTMLAttributes<HTMLTableElement> &
      ReactMarkdownProps
  >
) {
  const classes = useStyles();
  const { children } = props;
  const thead = children.filter((child: any) => child.type === 'thead')[0];
  const tbody = children.filter((child: any) => child.type === 'tbody')[0];

  return (
    <Paper variant="outlined" square>
      <Table className={classes.table} aria-label="a dense table">
        {React.isValidElement(thead) && markdownTableHead(thead.props)}
        {React.isValidElement(tbody) && markdownTableBody(tbody.props)}
      </Table>
    </Paper>
  );
}

const renderers = {
  code: MarkdownCode,
  table: MarkdownTable,
  tableHead: markdownTableHead,
  tableBody: markdownTableBody,
  tableRow: markdownTableRow,
  tableCell: markdownTableCell,
};

interface MarkdownProps {
  mdText: string;
}

/*
 * Document content is authored by API publishers and rendered for every
 * DevPortal visitor, including anonymous ones. `rehypeRaw` re-parses the raw
 * HTML embedded in that markdown into real DOM nodes, so the output MUST be
 * sanitized before it reaches the renderer. `defaultSchema` is the GitHub
 * allowlist shipped by `hast-util-sanitize`: it keeps the formatting markup
 * API documentation actually uses (tables, GFM task lists, `language-*` class
 * names on code blocks for Prism) and drops script/iframe/object/embed and
 * every `on*` event handler attribute.
 *
 * Plugin order matters: `rehypeRaw` must run first to build the HTML tree,
 * `rehypeSanitize` second to prune it. Reversing them leaves the raw HTML
 * unsanitized.
 */
const sanitizeSchema = defaultSchema;

export default function Markdown({ mdText }: MarkdownProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[gfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
      components={renderers}
      // eslint-disable-next-line react/no-children-prop
      children={mdText}
    />
  );
}
